import React, { useState, useEffect, useMemo } from 'react';
import "../styles/jobexplorer.css";
import { useAuth } from "../context/AuthContext";
import { authenticatedFetch } from '../context/apiClient';
import { useNavigate } from 'react-router-dom';

const JobExplorer = () => {
    const [jobs, setJobs] = useState([]);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({
        date: 'Select Date',
        model: 'Model',
        status: 'Status',
    });
    const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'descending' });
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 5;
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        const fetchJobs = async () => {
            if (!user?.token) return;
            setError(null);
            try {
                const response = await authenticatedFetch('/api/history', {}, user.token, logout);
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const data = await response.json();
                setJobs(data);
            } catch (e) {
                console.error("Failed to fetch jobs:", e);
                setError("Failed to load job history. Please try again later.");
            }
        };

        fetchJobs();
    }, [user, logout]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
        setCurrentPage(1);
    };

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const modelOptions = useMemo(() => [...new Set(jobs.map(j => j.target_llm).filter(Boolean))], [jobs]);
    const statusOptions = useMemo(() => [...new Set(jobs.map(j => j.status).filter(Boolean))], [jobs]);

    const filteredJobs = useMemo(() => {
        let filterableJobs = [...jobs];

        if (filters.status !== 'Status') {
            filterableJobs = filterableJobs.filter(job => job.status.toLowerCase() === filters.status.toLowerCase());
        }

        if (filters.model !== 'Model') {
            filterableJobs = filterableJobs.filter(job => job.target_llm === filters.model);
        }

        // Date filtering logic can be added here
        // For 'Last 7 days', 'Last 30 days', etc.

        return filterableJobs;
    }, [jobs, filters]);

    const sortedJobs = useMemo(() => {
        let sortableJobs = [...filteredJobs];
        if (sortConfig.key !== null) {
            sortableJobs.sort((a, b) => {
                if (a[sortConfig.key] < b[sortConfig.key]) {
                    return sortConfig.direction === 'ascending' ? -1 : 1;
                }
                if (a[sortConfig.key] > b[sortConfig.key]) {
                    return sortConfig.direction === 'ascending' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableJobs;
    }, [filteredJobs, sortConfig]);

    const paginatedJobs = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        return sortedJobs.slice(startIndex, startIndex + itemsPerPage);
    }, [sortedJobs, currentPage]);

    const totalPages = Math.ceil(sortedJobs.length / itemsPerPage);

    const getSortableClass = (name) => {
        if (sortConfig.key !== name) return 'sortable';
        return `sortable ${sortConfig.direction}`;
    };

    const viewTask = (id) => {
        navigate(`/assessment-report/${id}`);
    };
    const deleteTask = async (id) => {
        if (!window.confirm("Are you sure you want to delete this job?")) {
            return;
        }

        try {
            const response = await authenticatedFetch('/api/updatejob', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    scan_id: id,
                    status: 'CANCELED',
                }),
            }, user.token, logout);

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            setJobs(prevJobs => prevJobs.filter(job => job.scan_id !== id));
        } catch (e) {
            console.error("Failed to delete job:", e);
            setError("Failed to cancel the job. Please try again later.");
        }
    };

    return(
      <div className="job-explorer-page">  
        <div className="container">
                <div className="page-wrapper">
                
                    <div className="header">
                        <h1 className="page-title">Task Management</h1>
                        <div className="filters">
                            <div className="filter-select">
                                <svg className="filter-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                                </svg>
                                <select name="date" value={filters.date} onChange={handleFilterChange}>
                                    <option>Select Date</option>
                                    <option>Today</option>
                                    <option>Last 7 days</option>
                                    <option>Last 30 days</option>
                                    <option>Custom range</option>
                                </select>
                            </div>
                            <div className="filter-select">
                                <svg className="filter-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z"/>
                                </svg>
                                <select name="model" value={filters.model} onChange={handleFilterChange}>
                                    <option>Model</option>
                                    {modelOptions.map(m => <option key={m} value={m}>{m}</option>)}
                                </select>
                            </div>
                            <div className="filter-select">
                                <svg className="filter-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                                </svg>
                                <select name="status" value={filters.status} onChange={handleFilterChange}>
                                    <option>Status</option>
                                    {statusOptions.map(s => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </div>
                        </div>
                    </div>

                
                    <div className="table-container">
                        <table>
                            <thead>
                                <tr>
                                    <th className={getSortableClass('created_at')} onClick={() => requestSort('created_at')}>Submitted DateTime</th>
                                    <th className={getSortableClass('target_llm')} onClick={() => requestSort('target_llm')}>Model Name</th>
                                    <th className={getSortableClass('total_jailbroken')} onClick={() => requestSort('total_jailbroken')}>Issues/Total</th>
                                    <th className={getSortableClass('time_elapsed')} onClick={() => requestSort('time_elapsed')}>Time Elapsed</th>
                                    <th className={getSortableClass('status')} onClick={() => requestSort('status')}>Job Status</th>
                                    <th>Operate</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedJobs.map(job => {
                                    const progress = (job.total_jailbroken / job.total_queries) * 100;
                                    return (
                                        <tr key={job.scan_id}>
                                            <td className="datetime-cell">{new Date(job.created_at).toLocaleString()}</td>
                                            <td className="model-name">{job.target_llm}</td>
                                            <td>
                                                <div className="progress-wrapper">
                                                    <span className="progress-text">{job.total_jailbroken} / {job.total_queries}</span>
                                                    <div className="progress-bar-container">
                                                        <div className="progress-bar" style={{width: `${progress}%`}}></div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="time-cell">{parseFloat(job.time_elapsed).toFixed(2)}s</td>
                                            <td>
                                                <span className={`status-badge status-${job.status.toLowerCase()}`}>{job.status}</span>
                                            </td>
                                            <td>
                                                <div className="action-buttons">
                                                    <button className="btn btn-view" onClick={() => viewTask(job.scan_id)}>
                                                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                                                        </svg>
                                                        View
                                                    </button>
                                                    <button className="btn btn-delete" onClick={() => deleteTask(job.scan_id)}>
                                                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                                                        </svg>
                                                        Delete
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    )
                                })}
                                {paginatedJobs.length === 0 && (
                                    <tr>
                                        <td colSpan="6" style={{ textAlign: 'center', padding: '20px' }}>No jobs found.</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                        {error && (
                            <div className="text-red-500 text-center p-4">
                                {error}
                            </div>
                        )}
                    </div>

                
                    <div className="pagination">
                        <button 
                            className="pagination-btn" 
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                        >
                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{width: '18px', height: '18px'}}>
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"/>
                            </svg>
                        </button>
                        {[...Array(totalPages).keys()].map(pageNumber => (
                             <button 
                                key={pageNumber + 1}
                                className={`pagination-btn ${currentPage === pageNumber + 1 ? 'active' : ''}`}
                                onClick={() => setCurrentPage(pageNumber + 1)}
                            >
                                {pageNumber + 1}
                            </button>
                        ))}
                        <button 
                            className="pagination-btn"
                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                            disabled={currentPage === totalPages || totalPages === 0}
                        >
                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{width: '18px', height: '18px'}}>
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"/>
                            </svg>
                        </button>
                    </div>
                </div>
            </div>
        </div>    
    )
};

export default JobExplorer;
                                        
